// The thread pane's own pieces (ThreadViewModel / ThreadView, Windows ThreadPaneViewModel): the
// tri-state follow control, the typing slot (this thread's typists, else the chat's «en el chat»),
// and the postseen thread report that gives own replies their checks.
import { useCallback, useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { ThreadFollow, type FollowState } from '../../../core/threadFollow';
import { ThreadTyping } from '../../../core/threadTyping';
import { TypingTracker, pruneIntervalMillis } from '../../../core/typingTracker';
import { PostState, type PostRecord, type UserRecord } from '../../../core/records';
import { userDisplayName } from '../../../core/channelListDerivation';
import { t } from '../../../i18n/i18n';
import { Icon } from '../../../design/icons';
import './threads.css';

/**
 * Tri-state follow: null until the server answered (no control drawn), re-read on open and a moment
 * after the reader replies (the server auto-follows on reply). The toggle is optimistic.
 */
export function useThreadFollow(rootId: string, teamId: string, onError: (message: string) => void) {
  const [state, setState] = useState<FollowState>(null);
  const generation = useRef(0);
  const resolvedTeam = useRef('');

  const read = useCallback(async (gen: number) => {
    const team = resolvedTeam.current || (await data.call('threadTeamId', rootId, teamId));
    resolvedTeam.current = team;
    const answer = await data.call('threadFollowState', team, rootId).catch(() => null);
    if (gen === generation.current) setState((shown) => ThreadFollow.afterRead(shown, answer));
  }, [rootId, teamId]);

  useEffect(() => {
    const gen = ++generation.current;
    resolvedTeam.current = '';
    setState(null);
    void read(gen);
  }, [read]);

  const toggle = useCallback(async () => {
    const next = ThreadFollow.toggled(state);
    if (next === null) return;
    const gen = generation.current;
    setState(next);
    const team = resolvedTeam.current || (await data.call('threadTeamId', rootId, teamId));
    const took = await data.call('setThreadFollowing', team, rootId, next).catch(() => false);
    if (!took && gen === generation.current) {
      setState(!next);
      onError(t('web.thread.followFailed'));
    }
  }, [state, rootId, teamId, onError]);

  /** After a reply: the server's auto-follow lands first, then the state is re-read. */
  const afterReply = useCallback(() => {
    const gen = generation.current;
    setTimeout(() => void read(gen), ThreadFollow.REREAD_AFTER_REPLY_MS);
  }, [read]);

  return { state, toggle, afterReply };
}

export function FollowChip({ state, onToggle }: { state: FollowState; onToggle(): void }) {
  const key = ThreadFollow.labelKey(state);
  if (!key) return null;
  // Following is the state with weight (filled, accented, a check): it is the one that costs the
  // reader notifications (Android ThreadFollowChip). Not following is a quiet outline.
  return (
    <button type="button" className={`thread-follow-chip${state ? ' following' : ''}`} onClick={onToggle} aria-pressed={!!state} data-follow={state ? 'following' : 'not-following'}>
      {state ? <Icon name="check" size={13} strokeWidth={2.2} /> : null}
      {t(key)}
    </button>
  );
}

/** The thread's typing slot — its own TypingTracker fed by the same `typing` signals as the chat's. */
export function useThreadTyping(channelId: string, rootId: string, usersById: ReadonlyMap<string, UserRecord> | undefined): string | null {
  const tracker = useRef(new TypingTracker());
  const [, setTick] = useState(0);
  useEffect(() => {
    const off = data.onSignal((s) => {
      if (s.kind !== 'typing' || s.channelId !== channelId) return;
      if (s.parentId.startsWith('__clear__')) tracker.current.clear(s.userId, s.channelId, s.parentId.slice(9));
      else tracker.current.note(s.userId, s.channelId, s.parentId, Date.now());
      setTick((n) => n + 1);
    });
    const id = setInterval(() => {
      if (tracker.current.prune(Date.now())) setTick((n) => n + 1);
    }, pruneIntervalMillis);
    return () => {
      off();
      clearInterval(id);
    };
  }, [channelId]);
  const who = ThreadTyping.typists(tracker.current, channelId, rootId, Date.now());
  return ThreadTyping.label(who, (id) => {
    const u = usersById?.get(id);
    return u ? userDisplayName(u) : t('mac.unknownAuthor');
  });
}

export function ThreadTypingLine({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div className="typing thread-typing" role="status">
      <span className="typing-dots">
        <i />
        <i />
        <i />
      </span>
      {text}
    </div>
  );
}

/**
 * postseen, thread half: report the thread on open, and again whenever the reader's own synced
 * replies change (each new own reply asks for its check). The worker throttles both.
 */
export function useThreadSeen(channelId: string, rootId: string, posts: readonly PostRecord[] | undefined, me: string): void {
  const ownKey = (posts ?? [])
    .filter((p) => p.userId === me && p.state === PostState.Synced)
    .map((p) => p.id)
    .join(',');
  useEffect(() => {
    if (!channelId || !rootId) return;
    void data.call('threadSeen', channelId, rootId);
  }, [channelId, rootId, ownKey]);
  useEffect(() => () => void data.call('threadClosed', rootId), [rootId]);
}
